import { FormattedMessage, useIntl } from "react-intl";
import React from "react";
import {
  PlatformAutomationEnrollmentRow,
} from "../../../cores/platformAutomations/services/api";

type Props = {
  enrollments: PlatformAutomationEnrollmentRow[];
  onViewActivity: (enrollmentId: number) => void;
  onStop: (enrollmentId: number) => Promise<void>;
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString("es-CL") : "—";

export default function PlatformAutomationEnrollmentsPanel({
  enrollments,
  onViewActivity,
  onStop,
}: Props) {
  const intl = useIntl();

  const statusLabel = (status: string) =>
    intl.formatMessage({
      id: `app.platformAutomations.enrollment.status.${status}`,
      defaultMessage: status,
    });

  const triggerLabel = (source: string) =>
    intl.formatMessage({
      id: `app.platformAutomations.enrollment.trigger.${source}`,
      defaultMessage: source,
    });

  if (!enrollments.length) {
    return null;
  }

  return (
    <div className="email-mkt__table email-mkt__table--actions pa-enrollments-table">
      <div className="email-mkt__table-row email-mkt__table-row--head pa-enrollments-table__head">
        <span>
          <FormattedMessage id="app.platformAutomations.editor.colUser" />
        </span>
        <span>
          <FormattedMessage id="app.platformAutomations.editor.colType" />
        </span>
        <span>
          <FormattedMessage id="app.platformAutomations.editor.colStarted" />
        </span>
        <span>
          <FormattedMessage id="app.platformAutomations.editor.colProgress" />
        </span>
        <span>
          <FormattedMessage id="app.platformAutomations.editor.colCurrentStep" />
        </span>
        <span>
          <FormattedMessage id="app.platformAutomations.editor.colStatus" />
        </span>
        <span />
      </div>
      {enrollments.map((row) => {
        const progress =
          row.progress.emailsTotal > 0
            ? `${row.progress.emailsSent}/${row.progress.emailsTotal}`
            : String(row.progress.emailsSent);

        const endLabel =
          row.status === "completed"
            ? formatDate(row.completedAt)
            : row.status === "stopped" || row.status === "failed"
              ? `${formatDate(row.stoppedAt)}${
                  row.stopReason ? ` · ${row.stopReason}` : ""
                }`
              : row.nextScheduleAt
                ? intl.formatMessage(
                    { id: "app.platformAutomations.editor.colNextSend" },
                    { date: formatDate(row.nextScheduleAt) }
                  )
                : "—";

        return (
          <div className="email-mkt__table-row pa-enrollments-table__row" key={row.id}>
            <span className="pa-enrollments-table__user">
              <strong>#{row.id}</strong>
              <span>{row.userEmail || "—"}</span>
              {row.userName ? <small>{row.userName}</small> : null}
            </span>
            <span>
              <span
                className={`pa-run-type ${row.isTest ? "pa-run-type--test" : "pa-run-type--real"}`}
              >
                <FormattedMessage
                  id={
                    row.isTest
                      ? "app.platformAutomations.editor.runTypeTest"
                      : "app.platformAutomations.editor.runTypeReal"
                  }
                />
              </span>
              <small>{triggerLabel(row.triggerSource)}</small>
            </span>
            <span>
              {formatDate(row.triggeredAt)}
              {row.versionNumber != null && (
                <small>
                  <FormattedMessage
                    id="app.platformAutomations.editor.colVersion"
                    values={{ version: row.versionNumber }}
                  />
                </small>
              )}
            </span>
            <span>
              <strong>{progress}</strong>
              <small>
                <FormattedMessage id="app.platformAutomations.editor.colEmailsSent" />
              </small>
            </span>
            <span className="pa-enrollments-table__step">
              {row.status === "active"
                ? row.nextStepLabel || row.currentStepLabel || "—"
                : row.currentStepLabel || endLabel}
              {row.status === "active" && row.nextScheduleAt && (
                <small>{formatDate(row.nextScheduleAt)}</small>
              )}
            </span>
            <span>
              <span className={`pa-status-pill pa-status-pill--${row.status}`}>
                {statusLabel(row.status)}
              </span>
            </span>
            <span className="pa-enrollments-table__actions">
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={() => onViewActivity(row.id)}
              >
                <FormattedMessage id="app.platformAutomations.editor.viewActivity" />
              </button>
              {row.status === "active" && (
                <button
                  type="button"
                  className="email-mkt__ghost-btn pa-data-panel__danger"
                  onClick={() => onStop(row.id)}
                >
                  <FormattedMessage id="app.platformAutomations.editor.stopEnrollment" />
                </button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
